import React, {useEffect, useState} from 'react';
import {MdSubdirectoryArrowRight} from "react-icons/md";
import {EntityType, FinancialEntityType, useGetFinancialValuesLazyQuery, useGetNoteLazyQuery, VersionType} from '../../__generated__/generated_types';
import {BudgetingType} from '../../BudgetingType';
import {FinancialEntity} from '../../contexts/chartofaccounts/ChartOfAccountsContext';
import {createRequestVariables} from '../../contexts/chartofaccounts/CreateFinancialValuesQueryVariables';
import {useProperties} from '../../contexts/properties/PropertiesContext';
import {useSettings} from '../../contexts/settings/SettingsContext';
import {useVersions} from '../../contexts/versions/VersionsContext';
import AccountNoteModal, {AccountNoteModalFetchInputs} from '../note/AccountNoteModal';
import AccountNoteViewButton from '../note/AccountNoteViewButton';
import {AccountHeader} from './AccountHeader';
import {CategoryHeader} from './CategoryHeader';
import {ComponentHeader} from './ComponentHeader';
import {fillLineData, StarredFilter} from './Helpers';
import './Line.scss';
import {NumberCells} from './NumberCells';
import useYearDefinitions from './YearDefinitions';

export interface LineMonthData {
    monthIndex: number;
    value: number;
}
export interface ChildLinesData {
    linesData: LineData[] | undefined;
    isDataLoading?: boolean;
    hasChildren: boolean;
}

export interface LineData {
    // lineName: string;
    monthNumbers: LineMonthData[];
    total: number;
    referenceTotal: number;
    variance: number;
    variancePercent: number | null;
    // componentId?: string;
    // categoryId?: string;
    financialEntity: FinancialEntity;
    // isComponent?: boolean;
    // accountId?: string;
}

export interface LineProps {
    data: LineData;
    isPositiveGood: boolean;
    level?: number;
    tab: BudgetingType;
    varianceType: string;
    refreshTriggered: boolean;
    refreshCompletedCallback: () => void;
    budgetYear: number;
    starredFilter?: StarredFilter;
    onStarChanged?: (accountId: string, isStarred: boolean) => void;
}

export const Line: React.VFC<LineProps> = (propsIn) => {

    const props = {level: 0, ...propsIn}; // default level 0
    const [showChildren, setShowChildren] = useState(false);
    const {currentProperty: property} = useProperties();
    const [children, setChildren] = useState<LineData[] | undefined>();
    const {year: currentYear, startReforecastMonthIndex} = useSettings();
    const {currentDefinition: referenceYear} = useYearDefinitions();
    const {versions, getModelVersions} = useVersions();
    const [getFinancialValues, {called, loading, data, refetch}] = useGetFinancialValuesLazyQuery({
        notifyOnNetworkStatusChange: true,
        fetchPolicy: "no-cache",
        onCompleted: () => {
            if (!loading && props.refreshTriggered === true) {
                props.refreshCompletedCallback();
            }
        }
    });
    const [fetchNote, {data: noteData, refetch: refetchNote}] = useGetNoteLazyQuery({fetchPolicy: "network-only"});
    
    // used to set the currently active account note + driver notes being viewed/edited
    const [accountNoteModalInputs, setAccountNoteModalInputs] = useState<AccountNoteModalFetchInputs | null>(null);
    const [noteButtonText, setNoteButtonText] = useState<string>("");

    const hasChildren = props.data.financialEntity.children.length > 0;
    const isComponent = props.data.financialEntity.type == FinancialEntityType.Component;
    const isReforecast = props.tab === BudgetingType.REFORECAST;
    const starredFilterActive = props.starredFilter !== undefined;
    const isVisibleInStarredFilter = props.starredFilter?.visibleEntityIds.has(props.data.financialEntity.id) ?? false;

    function needCollapse() {
        setShowChildren(false);
    }

    function needExpand() {
        if (!called && property) {
            getFinancialValues({
                variables: createRequestVariables(props.data.financialEntity.children, property.id, startReforecastMonthIndex, currentYear, getModelVersions, props.budgetYear)
            });
        }
        setShowChildren(true);
    }

    useEffect(() => {
        if (called && !loading && data) {
            const convertedData: LineData[] = [];

            const {actualVersionId, reforecastVersionId, nextYearBudgetVersionId, currentYearTotalVersionId} = getModelVersions(currentYear);
            const valuesByEntityAndVersion = data.financialValues.financialValues.groupBy(row => row.entityId + "#" + row.versionId);
            const totalValuesByEntityAndVersion = data.financialValues.financialTotalValues.groupBy(row => row.entityId + "#" + row.versionId);

            props.data.financialEntity.children.forEach(child => {
                const values = isReforecast ?
                    [
                        ...(valuesByEntityAndVersion[child.id + "#" + actualVersionId] ?? []),
                        ...(valuesByEntityAndVersion[child.id + "#" + reforecastVersionId] ?? [])
                    ]
                    :
                    valuesByEntityAndVersion[child.id + "#" + nextYearBudgetVersionId] ?? [];

                let previousTotal = totalValuesByEntityAndVersion[child.id + "#" + referenceYear.versionId]?.firstElement?.value ?? "0";
                if(referenceYear.versionId === currentYearTotalVersionId) {
                    const reforecastValues = [
                        ...(valuesByEntityAndVersion[child.id + "#" + actualVersionId] ?? []),
                        ...(valuesByEntityAndVersion[child.id + "#" + reforecastVersionId] ?? [])
                    ];
                    previousTotal = reforecastValues.map(rv => Number(rv.value ?? 0.0)).sum();
                }

                const total = values.map(v => Number(v.value ?? 0.0)).sum();

                const lineData = fillLineData(
                    values.map(row => ({monthIndex: row.monthIndex, value: Number(row.value)})),
                    total,
                    previousTotal,
                    child
                );

                convertedData.push(lineData);
            });

            setChildren(convertedData);
        }

    }, [data, loading, property, isReforecast, referenceYear.versionId]);

    useEffect(() => {
        if (props.refreshTriggered === true && refetch) {
            refetch();
        }
    }, [props.refreshTriggered]);

    useEffect(() => {
        if (props.data.financialEntity.type === FinancialEntityType.Account && property) {
            fetchNote({
                variables: {
                    propertyId: property.id,
                    entityId: props.data.financialEntity.id,
                    entityType: EntityType.Account,
                    year: isReforecast ? props.budgetYear - 1 : props.budgetYear,
                    versionType: isReforecast ? VersionType.Reforecast : VersionType.Budget,
                }
            })
        }
    }, [props.data.financialEntity, property, isReforecast]);

    useEffect(() => {
        setNoteButtonText(noteData?.queryNote?.text ?? "");
    }, [noteData?.queryNote, isReforecast]);

    const rowName = props.data.financialEntity.name?.replace(/\s/g, '');

    return (
        <>
            {/* line */}
            <tr key={0} className={`${isComponent ? 'line-component' : ''} ${showChildren && props.level === 0 ? "bg-azure" : ""} tree-indent-${props.level}`}>
                {isComponent ?
                    <ComponentHeader name={`${props.data.financialEntity.name}${props.data.financialEntity.number ? " (" + props.data.financialEntity.number + ")" : ""}`} />
                    : hasChildren ?
                        <CategoryHeader
                            needCollapse={needCollapse}
                            needExpand={needExpand}
                            categoryName={`${props.data.financialEntity.name}${props.data.financialEntity.number ? " (" + props.data.financialEntity.number + ")" : ""}`}
                            level={props.level}
                            data={props.data?.financialEntity}
                            forceExpanded={starredFilterActive && isVisibleInStarredFilter}
                        />
                        :
                        <AccountHeader name={`${props.data.financialEntity.name}${props.data.financialEntity.number ? " (" + props.data.financialEntity.number + ")" : ""}`}
                            level={props.level}
                            accountId={props.data.financialEntity.id}
                            isReforecast={isReforecast}
                            rowName={rowName}
                            onStarredChange={(isStarred) => props.onStarChanged?.(props.data.financialEntity.id, isStarred)}
                        />
                }
                {/* line data */}
                {showChildren ?
                    <>
                        <td colSpan={12}></td>
                        <td className="as-total as-total-border-blue"></td>
                        <td colSpan={2}></td>
                    </>
                    :
                    <NumberCells monthNumbers={props.data.monthNumbers}
                        total={props.data.total}
                        referenceTotal={props.data.referenceTotal}
                        variance={props.data.variance}
                        variancePercent={props.data.variancePercent}
                        isPositiveGood={props.isPositiveGood}
                        varianceSelectedValue={props.varianceType}
                        isReforecast={isReforecast}
                        rowName={rowName}
                        reforecastStartMonth={property?.reforecastStartMonthIndex}
                    />
                }
                <td>
                    {props.data.financialEntity.type == FinancialEntityType.Account && property &&
                        <AccountNoteViewButton
                            notePreview={noteButtonText}
                            onActivate={() => setAccountNoteModalInputs({
                                propertyId: property.id,
                                accountId: props.data.financialEntity.id,
                                budgetYear: props.budgetYear,
                                versionType: isReforecast ? VersionType.Reforecast : VersionType.Budget,
                            })}
                        />
                    }
                </td>
            </tr>

            {/* children */}
            {showChildren && children && children
                .filter(child => !starredFilterActive ||
                    props.starredFilter?.visibleEntityIds.has(child.financialEntity.id))
                .map((child) =>
                    <Line key={child.financialEntity.id}
                        data={child}
                        isPositiveGood={props.isPositiveGood}
                        level={props.level + 1}
                        tab={props.tab}
                        varianceType={props.varianceType}
                        refreshTriggered={props.refreshTriggered}
                        refreshCompletedCallback={() => refetch && refetch()}
                        budgetYear={props.budgetYear}
                        starredFilter={props.starredFilter}
                        onStarChanged={props.onStarChanged}
                    />
                )}

            {/* line subtotal */}
            {showChildren && !starredFilterActive &&
                <tr key={1} className={"fw-bold " + (showChildren ? "bg-light-azure" : "")}>
                    <td className={`fw-bold tree-indent-${props.level + 1} bg-light-azure totals-expand p-0`}>
                        <div className="line-component-cell tree-line border-start-1 d-flex h-100 border-whisper border-start">
                            <MdSubdirectoryArrowRight className="arrow-right-icon" />
                            <div className="totals d-flex">{`Total ${props.data.financialEntity.name.toLocaleLowerCase()}`}</div>
                        </div>


                    </td>
                    <NumberCells monthNumbers={props.data.monthNumbers}
                        total={props.data.total}
                        referenceTotal={props.data.referenceTotal}
                        variance={props.data.variance}
                        variancePercent={props.data.variancePercent}
                        isPositiveGood={props.isPositiveGood}
                        varianceSelectedValue={props.varianceType}
                        isReforecast={isReforecast}
                        reforecastStartMonth={property?.reforecastStartMonthIndex}
                    />
                </tr>
            }
            {props.data.financialEntity.type == FinancialEntityType.Account && property &&
                <AccountNoteModal
                    accountName={rowName}
                    noteFetchInputs={accountNoteModalInputs}
                    onSave={() => {
                        setAccountNoteModalInputs(null);
                        refetchNote?.();
                    }}
                    onClose={() => setAccountNoteModalInputs(null)}
                />
            }
        </>
    );
};
